// pages/payroll/WeeklyBattaReport.jsx
import { useEffect, useContext, useMemo, useState } from "react";
import axios from "axios";
import HeaderBar from "../../components/HeaderBar";
import PayRollLayoutWrapper from "../../components/PayRollLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { Building2, Search, RefreshCcw, Printer, CalendarDays, MapPin, Users } from "lucide-react";

const BASE_URL = "http://nissi-india.com"; // change if needed

const WeeklyBattaReport = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const primaryBtnClass = `bg-${baseColor}-${baseShade}`;
  const primaryHoverClass = `hover:bg-${baseColor}-700`;

  const finYearId = localStorage.getItem("finYearId") || "";
  const branchId = localStorage.getItem("branchId") || ""; // adjust key if different in your app
  const branchType = localStorage.getItem("branchType") || "HO"; // adjust key if different in your app

  const screenBadge = useMemo(() => "INFRA SCREEN : C-36", []);

  const inputBase =
    `w-full px-4 py-3 rounded-2xl border outline-none transition ` +
    (isDark
      ? "bg-gray-800/40 border-white/10 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500"
      : "bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500");

  const labelBase = `text-sm font-semibold ${isDark ? "text-gray-200" : "text-gray-700"}`;

  const cardBase =
    `rounded-3xl border shadow-xl overflow-hidden ` +
    (isDark
      ? "border-white/10 bg-gradient-to-br from-white/15 to-white/5 backdrop-blur-md"
      : "border-gray-200 bg-white");

  const primaryBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl text-white font-bold shadow-lg ` +
    `hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${primaryBtnClass} ${primaryHoverClass}`;

  const ghostBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl font-bold border transition-all duration-200 ` +
    (isDark ? "border-white/10 text-white hover:bg-white/10" : "border-gray-200 text-gray-800 hover:bg-gray-50");

  // ---- Meta
  const [metaLoading, setMetaLoading] = useState(false);
  const [branches, setBranches] = useState([]); // [{value,label}]
  const [departments, setDepartments] = useState([]); // [{value,label}]
  const [lockBranch, setLockBranch] = useState(false);

  // ---- Filters
  const [empCode, setEmpCode] = useState("");
  const [empName, setEmpName] = useState("");
  const [location, setLocation] = useState("Select");
  const [department, setDepartment] = useState("Select");
  const [fromDate, setFromDate] = useState(""); // YYYY-MM-DD
  const [toDate, setToDate] = useState(""); // YYYY-MM-DD

  // ---- Autocomplete
  const [empCodeSug, setEmpCodeSug] = useState([]);
  const [empNameSug, setEmpNameSug] = useState([]);
  const [showEmpCodeSug, setShowEmpCodeSug] = useState(false);
  const [showEmpNameSug, setShowEmpNameSug] = useState(false);

  // ---- Table + pagination
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({ overtimeSum: 0, totalValueSum: 0 });
  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const fetchMeta = async () => {
    if (!String(finYearId).trim()) {
      toast.warn("Please select Financial Year.");
      return;
    }
    if (!String(branchId).trim() || !String(branchType).trim()) {
      toast.warn("Branch info missing in localStorage (branchid/branchtype).");
      return;
    }

    try {
      setMetaLoading(true);
      const res = await axios.get(`${BASE_URL}/api/weekly-batta/meta`, {
        params: {
          finYearId: Number(finYearId),
          branchId: Number(branchId),
          branchType: String(branchType),
        },
      });

      if (res?.data?.success) {
        setBranches(res.data.branches || []);
        setDepartments(res.data.departments || []);
        setLockBranch(!!res.data.lockBranch);

        // defaults for date
        setFromDate(res.data.defaults?.fromDate || "");
        setToDate(res.data.defaults?.toDate || "");

        // default branch value
        const defBranch = res.data.defaultBranchValue || "Select";
        setLocation(defBranch || "Select");
      } else {
        toast.error(res?.data?.message || "Failed to load meta.");
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Something went wrong.";
      toast.error(msg);
    } finally {
      setMetaLoading(false);
    }
  };

  const fetchList = async (p = 1) => {
    if (!String(finYearId).trim()) {
      toast.warn("Please select Financial Year.");
      return;
    }

    try {
      setLoading(true);

      const res = await axios.get(`${BASE_URL}/api/weekly-batta/data`, {
        params: {
          finYearId: Number(finYearId),
          empCode: empCode.trim(),
          empName: empName.trim(),
          department,
          location,
          fromDate,
          toDate,
          page: p,
          pageSize,
        },
      });

      if (res?.data?.success) {
        setRows(res.data.rows || []);
        setTotals(res.data.totals || { overtimeSum: 0, totalValueSum: 0 });
        setTotal(res.data.total || 0);
        setTotalPages(res.data.totalPages || 1);
        setPage(res.data.page || p);
      } else {
        toast.error(res?.data?.message || "Failed to load weekly batta report.");
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Something went wrong.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // initial meta + initial list
    fetchMeta().then(() => {
      // list will be loaded after meta (dates/branch default)
      // but state updates are async; so call list in small timeout for safety
      setTimeout(() => fetchList(1), 50);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // page change only
    fetchList(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  // ---- Autocomplete calls (simple debounce)
  useEffect(() => {
    const t = setTimeout(async () => {
      if (!String(finYearId).trim()) return;
      const q = empCode.trim();
      if (!q || q.length < 1) {
        setEmpCodeSug([]);
        return;
      }
      try {
        const res = await axios.get(`${BASE_URL}/api/weekly-batta/empcode-suggest`, {
          params: { finYearId: Number(finYearId), prefix: q },
        });
        if (res?.data?.success) setEmpCodeSug(res.data.items || []);
      } catch {
        // silent
      }
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empCode]);

  useEffect(() => {
    const t = setTimeout(async () => {
      if (!String(finYearId).trim()) return;
      const q = empName.trim();
      if (!q || q.length < 1) {
        setEmpNameSug([]);
        return;
      }
      try {
        const res = await axios.get(`${BASE_URL}/api/weekly-batta/empname-suggest`, {
          params: { finYearId: Number(finYearId), prefix: q },
        });
        if (res?.data?.success) setEmpNameSug(res.data.items || []);
      } catch {
        // silent
      }
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empName]);

  const handleSearch = async (e) => {
    e?.preventDefault?.();

    // Minimal validation (ASP had none; but dates should exist)
    if (!fromDate || !toDate) {
      toast.warn("Please select From Date and To Date");
      return;
    }
    setPage(1);
    await fetchList(1);
  };

  const handleRefresh = async () => {
    await fetchMeta();
    setEmpCode("");
    setEmpName("");
    setDepartment("Select");
    // keep location set by meta (and locked if needed)
    setPage(1);
    await fetchList(1);
  };

  const handlePrint = () => {
    if (!String(finYearId).trim()) {
      toast.warn("Please select Financial Year.");
      return;
    }
    if (!fromDate || !toDate) {
      toast.warn("Please select From Date and To Date");
      return;
    }

    // React print page route (you can create later)
    // It can call /api/weekly-batta/print-data using query params.
    const url =
      `/PayRoll/WeeklyBattaReport_Print?` +
      `finYearId=${encodeURIComponent(finYearId)}` +
      `&empCode=${encodeURIComponent(empCode.trim())}` +
      `&empName=${encodeURIComponent(empName.trim())}` +
      `&department=${encodeURIComponent(department)}` +
      `&location=${encodeURIComponent(location)}` +
      `&fromDate=${encodeURIComponent(fromDate)}` +
      `&toDate=${encodeURIComponent(toDate)}`;

    window.open(url, "_blank", "width=900,height=900,scrollbars=yes,resizable=no");
  };

  const showingFrom = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const showingTo = Math.min(page * pageSize, total);

  return (
    <PayRollLayoutWrapper>
      <ToastContainer
        position="top-right"
        autoClose={2800}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme={isDark ? "dark" : "light"}
      />

      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">WEEKLY BATTA REPORT</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`mx-auto max-w-7xl p-6 mt-6 rounded-3xl shadow-2xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `${currentSkin.glass} bg-white text-black border-gray-200`
          }`}
        >
          {/* Top row */}
          <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4 mb-6">
            <div className="flex flex-wrap items-center gap-4">
              <div
                className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow ${
                  isDark ? "bg-white/10" : "bg-gray-100"
                }`}
              >
                <Building2 className={isDark ? "text-white" : "text-gray-700"} />
              </div>

              <div>
                <div className={`text-2xl font-extrabold ${isDark ? "text-white" : "text-gray-900"}`}>
                  Weekly Batta Report
                </div>
                <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  Filters • Search • Print • Pagination
                </div>
              </div>

              <div
                className={`px-4 py-2 rounded-2xl text-sm font-bold ${
                  isDark ? "bg-blue-900/40 text-blue-100" : "bg-blue-100 text-blue-800"
                }`}
              >
                {screenBadge}
              </div>

              <div
                className={`px-4 py-2 rounded-2xl text-sm font-bold ${
                  isDark ? "bg-emerald-900/40 text-emerald-100" : "bg-emerald-100 text-emerald-800"
                }`}
              >
                {total} Records
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 justify-end">
              <button onClick={handleRefresh} className={ghostBtn} disabled={loading || metaLoading}>
                <RefreshCcw className={`w-4 h-4 ${loading || metaLoading ? "animate-spin" : ""}`} />
                Refresh
              </button>

              <button onClick={handlePrint} className={ghostBtn} disabled={loading || metaLoading}>
                <Printer className="w-4 h-4" />
                Print
              </button>
            </div>
          </div>

          {/* Filters */}
          <form onSubmit={handleSearch} className={cardBase + " p-6 mb-6"}>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
              <div className="relative">
                <label className={labelBase}>Employee Code</label>
                <input
                  value={empCode}
                  onChange={(e) => {
                    setEmpCode(e.target.value);
                    setShowEmpCodeSug(true);
                  }}
                  onFocus={() => setShowEmpCodeSug(true)}
                  onBlur={() => setTimeout(() => setShowEmpCodeSug(false), 150)}
                  placeholder="Type Employee Code..."
                  className={`mt-2 ${inputBase}`}
                />
                {showEmpCodeSug && empCodeSug.length > 0 && (
                  <div
                    className={`absolute z-20 mt-2 w-full rounded-2xl border shadow-lg overflow-hidden ${
                      isDark ? "bg-gray-900 border-white/10" : "bg-white border-gray-200"
                    }`}
                  >
                    {empCodeSug.slice(0, 10).map((it) => (
                      <button
                        key={it}
                        type="button"
                        onClick={() => {
                          setEmpCode(it);
                          setShowEmpCodeSug(false);
                        }}
                        className={`w-full text-left px-4 py-2 text-sm ${
                          isDark ? "hover:bg-white/10 text-gray-200" : "hover:bg-gray-50 text-gray-800"
                        }`}
                      >
                        {it}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="relative">
                <label className={labelBase}>Employee Name</label>
                <input
                  value={empName}
                  onChange={(e) => {
                    setEmpName(e.target.value);
                    setShowEmpNameSug(true);
                  }}
                  onFocus={() => setShowEmpNameSug(true)}
                  onBlur={() => setTimeout(() => setShowEmpNameSug(false), 150)}
                  placeholder="Type Employee Name..."
                  className={`mt-2 ${inputBase}`}
                />
                {showEmpNameSug && empNameSug.length > 0 && (
                  <div
                    className={`absolute z-20 mt-2 w-full rounded-2xl border shadow-lg overflow-hidden ${
                      isDark ? "bg-gray-900 border-white/10" : "bg-white border-gray-200"
                    }`}
                  >
                    {empNameSug.slice(0, 10).map((it) => (
                      <button
                        key={it}
                        type="button"
                        onClick={() => {
                          setEmpName(it);
                          setShowEmpNameSug(false);
                        }}
                        className={`w-full text-left px-4 py-2 text-sm ${
                          isDark ? "hover:bg-white/10 text-gray-200" : "hover:bg-gray-50 text-gray-800"
                        }`}
                      >
                        {it}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <label className={labelBase}>
                  Branch <span className="text-xs opacity-70">(Location)</span>
                </label>
                <div className="mt-2 flex items-center gap-2">
                  <MapPin className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
                  <select
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    className={inputBase}
                    disabled={lockBranch}
                  >
                    <option value="Select">Select</option>
                    {branches.map((b) => (
                      <option key={b.value} value={b.value}>
                        {b.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className={labelBase}>Department</label>
                <div className="mt-2 flex items-center gap-2">
                  <Users className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
                  <select value={department} onChange={(e) => setDepartment(e.target.value)} className={inputBase}>
                    <option value="Select">Select</option>
                    {departments.map((d) => (
                      <option key={d.value} value={d.value}>
                        {d.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className={labelBase}>From Date</label>
                <div className="mt-2 flex items-center gap-2">
                  <CalendarDays className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
                  <input
                    type="date"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    className={inputBase}
                  />
                </div>
              </div>

              <div>
                <label className={labelBase}>To Date</label>
                <div className="mt-2 flex items-center gap-2">
                  <CalendarDays className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
                  <input
                    type="date"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    className={inputBase}
                  />
                </div>
              </div>

              <div className="md:col-span-2 xl:col-span-2 flex items-end justify-end gap-3">
                <button type="submit" className={primaryBtn} disabled={loading || metaLoading}>
                  <Search className="w-4 h-4" />
                  Search
                </button>
              </div>
            </div>
          </form>

          {/* Table */}
          <div className={cardBase}>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={`${primaryBtnClass} text-white`}>
                  <tr>
                    <th className="px-4 py-4 text-center font-bold">S.No</th>
                    <th className="px-4 py-4 text-center font-bold">Employee Code</th>
                    <th className="px-4 py-4 text-center font-bold">Employee Name</th>
                    <th className="px-4 py-4 text-center font-bold">Designation</th>
                    <th className="px-4 py-4 text-center font-bold">Days Worked</th>
                    <th className="px-4 py-4 text-center font-bold">Batta Amount</th>
                    <th className="px-4 py-4 text-center font-bold">OverTime</th>
                    <th className="px-4 py-4 text-center font-bold">Total value</th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {loading ? (
                    Array.from({ length: pageSize }).map((_, idx) => (
                      <tr key={idx} className={isDark ? "bg-gray-900" : "bg-white"}>
                        {Array.from({ length: 8 }).map((__, c) => (
                          <td key={c} className="px-4 py-4">
                            <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded animate-pulse" />
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : rows.length === 0 ? (
                    <tr>
                      <td colSpan={8} className={`px-4 py-12 text-center ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                        No Records Found
                      </td>
                    </tr>
                  ) : (
                    rows.map((r, idx) => (
                      <tr
                        key={`${r.fld_employeecode}-${idx}`}
                        className={`${isDark ? "bg-gray-900" : "bg-white"} hover:${isDark ? "bg-gray-800" : "bg-gray-50"}`}
                      >
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {(page - 1) * pageSize + idx + 1}
                        </td>
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.fld_employeecode}
                        </td>
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.fld_employeename}
                        </td>
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.fld_desgination}
                        </td>
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.fld_daysworked}
                        </td>
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.fld_battaamount}
                        </td>
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.fld_overtime}
                        </td>
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.fld_total}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>

                {/* Footer totals (match ASP footer: overtime + total value) */}
                {rows.length > 0 && !loading && (
                  <tfoot>
                    <tr className={isDark ? "bg-gray-900" : "bg-white"}>
                      <td className="px-4 py-4 font-bold text-right" colSpan={6}>
                        Total
                      </td>
                      <td className="px-4 py-4 font-bold text-center">{totals.overtimeSum}</td>
                      <td className="px-4 py-4 font-bold text-center">{totals.totalValueSum}</td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>

          {/* Bottom row: pagination */}
          <div className="flex flex-col md:flex-row items-center justify-between mt-6 gap-4">
            <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
              Showing <b>{showingFrom}</b> to <b>{showingTo}</b> of <b>{total}</b>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              {totalPages > 1 && (
                <div className="inline-flex rounded-2xl overflow-hidden border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className={`px-4 py-2 text-sm font-bold ${
                      page === 1
                        ? "text-gray-400 cursor-not-allowed"
                        : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                    }`}
                  >
                    Prev
                  </button>

                  {[...Array(totalPages)].map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setPage(i + 1)}
                      className={`px-4 py-2 text-sm font-bold transition ${
                        page === i + 1 ? `${primaryBtnClass} text-white` : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                      }`}
                    >
                      {i + 1}
                    </button>
                  ))}

                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className={`px-4 py-2 text-sm font-bold ${
                      page === totalPages
                        ? "text-gray-400 cursor-not-allowed"
                        : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                    }`}
                  >
                    Next
                  </button>
                </div>
              )}

              <button onClick={() => fetchList(page)} className={ghostBtn} disabled={loading || metaLoading}>
                <RefreshCcw className={`w-4 h-4 ${loading || metaLoading ? "animate-spin" : ""}`} />
                Refresh Grid
              </button>
            </div>
          </div>
        </div>
      </div>
    </PayRollLayoutWrapper>
  );
};

export default WeeklyBattaReport;